/**
 * Reset Password Page
 * Set new password using reset token
 */

import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Lock, CheckCircle2, XCircle, KeyRound, Eye, EyeOff } from 'lucide-react';
import { authApi } from '../../lib/api';
import vizzexLogo from '../../assets/VIZZEX.250.png';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md animate-slide-up">
          <div className="text-center mb-8">
            <img 
              src={vizzexLogo} 
              alt="VizzEx Pro" 
              className="w-[250px] mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          </div>

          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-[var(--color-error)]" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Reset Link</h2>
            <p className="text-[var(--text-secondary)] mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.resetPassword(token, password);
      
      if (response.success) {
        setSuccess(true);
        // Redirect to login after delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      setError(error.response?.data?.error || error.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={vizzexLogo}
            alt="VizzEx Pro"
            className="w-[250px] mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          <p className="text-[var(--text-secondary)] mt-2">Create new password</p>
        </div>

        <div className="glass rounded-2xl p-8">
          {success ? (
            // Success state
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Password Reset!</h2>
              <p className="text-[var(--text-secondary)] mb-4">
                Your password has been successfully reset.
              </p>
              <p className="text-sm text-white/70">
                Redirecting to login...
              </p>
            </div>
          ) : (
            // Reset form
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-3">
                  <KeyRound className="w-6 h-6 text-[var(--color-primary-400)]" />
                </div>
                <p className="text-[var(--text-secondary)] text-sm">
                  Enter your new password below.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-[var(--color-error)] text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[var(--color-primary-400)] focus:ring-1 focus:ring-[var(--color-primary-400)] transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={12}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--bg-tertiary)] rounded"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-white/70" />
                    ) : (
                      <Eye className="w-4 h-4 text-white/70" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-white/70 mt-1">At least 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg focus:border-[var(--color-primary-400)] focus:ring-1 focus:ring-[var(--color-primary-400)] transition-colors"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}


